import { CombinedGraphQLErrors } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { isNestJsGraphQLError, logErrorMessages } from '@pcg/graphql-kit';

import { goToLogin } from '@/shared/tools/auth';
import { generateRequestId } from '@dott/core';

import { CLIENT_ORIGIN_ID, ORIGIN_ID_HEADER } from './origin.ts';

export const apolloRequestIdLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'x-request-id': generateRequestId(),
    },
  };
});

/**
 * Stamp every operation with the id of the tab that issued it.
 *
 * Unlike `x-request-id` (unique per request, for tracing) this is stable for the
 * page load: the API copies it onto the live-update events a write produces, so
 * this tab can ignore its own echo while still reacting to everything else the
 * same user does elsewhere. See `origin.ts` and `useLiveUpdates`.
 */
export const apolloOriginIdLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      [ORIGIN_ID_HEADER]: CLIENT_ORIGIN_ID,
    },
  };
});

export const apolloErrorLink = onError((handlerOpts) => {
  const { error } = handlerOpts;

  if (!CombinedGraphQLErrors.is(error)) {
    return;
  }

  const unauthenticated = error.errors.some(
    (err) =>
      isNestJsGraphQLError(err)
      // A dead 2FA-pending cookie is a 401, but it is NOT a dead session —
      // the visitor has no session yet, they are mid-sign-in. `/login` and
      // `/magic-link` own this key and turn it into "start over" UI; a
      // redirect from here would race them and blank the message.
      && err.extensions.key !== 'AUTH_2FA_PENDING_TOKEN_INVALID'
      && (err.extensions.statusCode === 401
        || err.extensions.key === 'SESSION_INVALID'
        || err.extensions.key === 'SESSION_EXPIRED'
        || err.extensions.key === 'ACCESS_TOKEN_EXPIRED'
        || err.extensions.key === 'ACCESS_TOKEN_INVALID'),
  );

  if (unauthenticated) {
    goToLogin();

    return;
  }

  for (const err of error.errors) {
    if (!isNestJsGraphQLError(err)) {
      continue;
    }

    logErrorMessages(handlerOpts);
  }
});
